import { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { buildQuickFamilyPayload } from '../../utils/taxonomyQuickCreate';

const EMPTY = {
  id: '',
  label: '',
  icon: '',
  hint: '',
  sortOrder: 0,
};

/**
 * Modale création / édition famille taxonomie.
 * variant="quick" : formulaire picker (libellé seul, code auto).
 * variant="full" (défaut) : page Familles & métiers.
 */
export default function FamilyFormModal({
  open,
  onClose,
  onSubmit,
  initial = null,
  submitting = false,
  variant = 'full',
  existingFamilyIds = [],
}) {
  const isQuick = variant === 'quick';
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(EMPTY);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        id: initial.id || '',
        label: initial.label || '',
        icon: initial.icon || '',
        hint: initial.hint || '',
        sortOrder: initial.sortOrder ?? 0,
      });
    } else {
      setForm(EMPTY);
    }
    setFieldError('');
  }, [open, initial]);

  const handleSubmit = async (e) => {
    // Empêche le submit du <form> commerçant parent (modales imbriquées).
    e.preventDefault();
    e.stopPropagation();
    if (submitting) return;
    const label = form.label.trim();
    if (!label) {
      setFieldError('Le libellé est obligatoire.');
      return;
    }

    if (isQuick && !isEdit) {
      try {
        await onSubmit(buildQuickFamilyPayload(label, { existingFamilyIds }));
      } catch (err) {
        setFieldError(err?.message || 'Création impossible.');
      }
      return;
    }

    if (!isEdit) {
      const id = form.id.trim().toLowerCase();
      if (!id || !/^[a-z][a-z0-9_]*$/.test(id)) {
        setFieldError('Code famille : minuscules, chiffres et _ (ex. restauration).');
        return;
      }
      await onSubmit({
        id,
        label,
        icon: form.icon.trim() || null,
        hint: form.hint.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
      });
      return;
    }
    await onSubmit({
      label,
      icon: form.icon.trim() || null,
      hint: form.hint.trim() || null,
      sortOrder: Number(form.sortOrder) || 0,
    });
  };

  const title = isQuick
    ? 'Créer une famille'
    : isEdit
      ? 'Modifier la famille'
      : 'Ajouter une famille';

  return (
    <Modal isOpen={open} onClose={onClose} title={title} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
        {fieldError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {fieldError}
          </p>
        )}

        {isQuick && !isEdit ? (
          <>
            <p className="text-xs text-gray-500">
              Saisissez un nom. Le code est généré automatiquement. Les réglages avancés
              restent dans Familles &amp; métiers.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de la famille *
              </label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ex. Cuisine du monde"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
                autoFocus
              />
            </div>
          </>
        ) : (
          <>
            {!isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code (id) *
                </label>
                <input
                  type="text"
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value })}
                  placeholder="restauration"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={submitting}
                />
                <p className="text-xs text-gray-500 mt-1">Immuable après création.</p>
              </div>
            )}
            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <p className="text-sm text-gray-600 font-mono">{form.id}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Libellé *</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
              <input
                type="text"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="🍽️"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indication (hint)
              </label>
              <textarea
                value={form.hint}
                onChange={(e) => setForm({ ...form, hint: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ordre d&apos;affichage
              </label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm"
                disabled={submitting}
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type={isQuick && !isEdit ? 'button' : 'submit'}
            disabled={submitting}
            onClick={
              isQuick && !isEdit
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(e);
                  }
                : undefined
            }
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
          >
            {submitting ? 'Création…' : isQuick && !isEdit ? 'Créer' : isEdit ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
